import { useContext, useEffect, useCallback } from "react";
import IUpdateSupplierDTO from "../../../models/DTO/IUpdateSupplierDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ISupplierModel from "../../../models/ISupplierModel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import SupplierProfileOptions from "./SupplierProfileOptions/SupplierProfileOptions";
import IOnUpdateSupplierDTO from "../../../models/DTO/IOnUpdateSupplierDTO";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import { SocketContext } from "@contexts/SocketContext";
import UiContext from "@contexts/UiContext";
import IUiSocketSetup from "@models/IUiSocketSetup";
import { ConfirmWorkflowChangeModal } from "./ConfirmWorkflowChangeModal/ConfirmWorkflowChangeModal";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import { useNavigate } from "react-router";

const BusinessSettings = () => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const navigate = useNavigate();

    const onUpdateSupplier = (data: IOnUpdateSupplierDTO) => {
        const notification: INotification = {
            message: "successfully_updated_settings",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        // Close any open modals when update is complete
        closeModal();
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.SupplierOnUpdateSupplier, fn: onUpdateSupplier }];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    /**
     * `changes` holds only the fields edited on the page since its last save; the API merges them
     * into the stored supplier, so two users saving different settings at once no longer overwrite
     * each other with stale copies of the whole model.
     */
    const updateSupplier = (changes: Partial<ISupplierModel>) => {
        const payload: IUpdateSupplierDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            supplierModel: changes,
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplier, payload);
    };

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const createConfirmSubmit = (changes: Partial<ISupplierModel>) => () => {
        updateSupplier(changes);
        closeModal();
    };

    const cancelSubmit = useCallback(() => {
        closeModal();
    }, []);

    const openModal = (changes: Partial<ISupplierModel>) => {
        const modalComponent = <ConfirmWorkflowChangeModal confirmSubmit={createConfirmSubmit(changes)} cancelSubmit={cancelSubmit} />;

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const performSave = (changes: Partial<ISupplierModel>, hasOrderProgressStateChanges?: boolean) => {
        if (hasOrderProgressStateChanges) {
            openModal(changes);
        } else {
            // As a safety, the workflow states never go across unless explicitly changed.
            const { order_progress_states, ...rest } = changes;

            updateSupplier(rest);
        }
    };

    return (
        <div className="BusinessSettings">
            <div className={"row"}>
                <div className={"col-12 "}>
                    <SupplierProfileOptions
                        userModel={userModel}
                        supplierModel={supplierModel}
                        saveFn={performSave}
                        socket={socket}
                        notifyFn={uiContext.notifyFn}
                        toggleSidebar={uiContext.toggleSidebar}
                        setSidebarComponent={uiContext.setSidebarComponent}
                        setActivityIndicator={uiContext.setActivityIndicator}
                        showHiddenFeatures={uiContext.showHiddenFeatures}
                        navigate={navigate}
                    />
                </div>
            </div>
        </div>
    );
};

export default BusinessSettings;
